import type { ScannerSource } from '../shared/desktop-api';
import type { PaigeIntakeKind } from './paige.global';
import { openScanSession, stopScanSession, type StageFiles } from './scan-session';

/**
 * What the "Add pages" picker offers beside files and folders: named plainly, each one a sheet, not
 * a device. The desktop's list is scanners, so its tiles read "Scan paper" over the device's name;
 * Paige's second entry is e-mail to this job, which is not scanning paper. Each source carries its
 * own tile heading and art (`ScannerSource.title`, `.icon`), because the two tiles both reading
 * "Scan paper" hid the most useful way into a job behind the least useful word for it.
 */
const sources: { kind: PaigeIntakeKind; source: ScannerSource }[] = [
  {
    kind: 'scan',
    source: {
      name: 'Scan paper with your scanner (Paige Scan)',
      hostArchitecture: 'x64',
      title: 'Scan paper',
      icon: 'scanner',
    },
  },
  {
    kind: 'email',
    source: {
      name: 'Email, to this job’s address',
      hostArchitecture: 'x64',
      title: 'Email it in',
      icon: 'email',
    },
  },
  {
    kind: 'folder',
    source: {
      name: 'Files saved to a folder, sent in by Paige Scan',
      hostArchitecture: 'x64',
      title: 'Watch a folder',
      icon: 'folder',
    },
  },
];

/**
 * The host sources the browser lists open a sheet in the workbench (IntakeSheet). The scanner
 * one is a real scan: Paige Scan on this computer scans, the sheet uploads the pages through the
 * session with `stage`, and `stageScan` answers with the staged capture the screen then drains;
 * closing the sheet answers canceled, which CapturePoint reads as the operator's own decision. The
 * email and folder ones stage nothing: what a watched folder sends arrives on its own.
 */
export function intakeSources(stage: StageFiles): Record<string, unknown> {
  return {
    getScannerSources: async () => ({ sources: sources.map((s) => s.source), errors: [] }),
    getSimulatedScanners: async () => [],
    stageScan: async (request: { jobId: string; source: { name: string } }) => {
      const kind = sources.find((s) => s.source.name === request.source.name)?.kind;
      if (!kind) return { canceled: true };
      const outcome = kind === 'scan' ? openScanSession(request.jobId, stage) : { canceled: true };
      window.paigeWorkbench?.showIntake?.(kind);
      return outcome;
    },
    // The screen's own Stop buttons show for as long as the scan sheet is open (scan-session.ts).
    cancelScan: async () => stopScanSession(),
  };
}
